import { createRouter as createVueRouter, createMemoryHistory, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { setupGuards } from '@/router/guards';
import { PERMISSIONS } from '@/constants/permissions';
import LinkPageHostPage from '@/sites/tfs/modules/link-page/pages/LinkPageHostPage.vue';
import type { LinkPageHost } from '@/modules/link-page/domain/entities/LinkPage';

const routes: RouteRecordRaw[] = [
  // Public Routes
  {
    path: '/',
    name: 'home',
    component: () => import('@/sites/tfs/views/HomeView.vue'),
    meta: { layout: 'public' },
  },
  {
    path: '/design-system',
    name: 'design-system-demo',
    component: () => import('@/sites/tfs/views/DesignSystemDemoView.vue'),
    meta: { layout: 'public' },
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('@/sites/tfs/views/MaintenanceView.vue'),
    meta: { layout: 'blank', title: 'maintenance.routeTitle' },
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('@/sites/tfs/views/ContactView.vue'),
    meta: { layout: 'public', title: 'Contact Us' },
  },
  {
    path: '/info/terms',
    name: 'terms',
    component: () => import('@/sites/tfs/views/TermsView.vue'),
    meta: { layout: 'public', title: 'Terms and Conditions' },
  },
  {
    path: '/info/privacy',
    name: 'privacy',
    component: () => import('@/sites/tfs/views/PrivacyView.vue'),
    meta: { layout: 'public', title: 'Privacy Policy' },
  },
  {
    path: '/auth/register',
    name: 'register',
    component: () => import('@/sites/tfs/views/auth/RegisterView.vue'),
    meta: { layout: 'public', guest: true },
  },
  {
    path: '/auth/callback',
    name: 'oauth-callback',
    component: () => import('@/sites/tfs/views/auth/OAuthCallbackView.vue'),
    meta: { guest: true },
  },
  {
    path: '/auth/forgot-password',
    name: 'forgot-password',
    component: () => import('@/sites/tfs/views/auth/ForgotPasswordView.vue'),
    meta: { layout: 'public', guest: true },
  },
  {
    path: '/auth/reset-password',
    name: 'reset-password',
    component: () => import('@/sites/tfs/views/auth/ResetPasswordView.vue'),
    meta: { layout: 'public', guest: true },
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/sites/tfs/views/errors/ForbiddenView.vue'),
    meta: { layout: 'public', requiresAuth: true, title: 'Access Denied' },
  },
  {
    path: '/auth/verify-email',
    name: 'verify-email',
    component: () => import('@/sites/tfs/views/auth/VerifyEmailView.vue'),
    meta: { layout: 'public' },
  },
  // User Settings Routes
  {
    path: '/settings',
    redirect: '/settings/account',
    meta: { requiresAuth: true },
    children: [
      {
        path: 'account',
        name: 'settings-account',
        component: () => import('@/sites/tfs/views/Settings/AccountView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'profile',
        name: 'settings-profile',
        component: () => import('@/sites/tfs/views/Settings/ProfileManagementView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'links',
        name: 'settings-links',
        component: () => import('@/sites/tfs/modules/link-page/pages/LinkPageSettingsPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'notifications',
        name: 'settings-notifications',
        component: () => import('@/sites/tfs/views/Settings/NotificationsCenterView.vue'),
        meta: { requiresAuth: true },
      },
    ],
  },
  // My Account Hub
  {
    path: '/my-account',
    name: 'my-account',
    component: () => import('@/sites/tfs/views/user/MyAccountView.vue'),
    meta: { requiresAuth: true, title: 'My Account' },
  },
  // My Downloads Route (canonical)
  {
    path: '/my-downloads',
    name: 'my-downloads',
    component: () => import('@/sites/tfs/views/user/DownloadsView.vue'),
    meta: { layout: 'public', requiresAuth: true, title: 'My Downloads' },
  },
  // Legacy redirect
  {
    path: '/user/downloads',
    redirect: '/my-downloads',
  },
  {
    path: '/galleries',
    name: 'galleries',
    component: () => import('@/sites/tfs/views/galleries/GalleriesView.vue'),
    meta: { layout: 'public' },
  },
  {
    path: '/category/:slug',
    name: 'public-category',
    component: () => import('@/sites/tfs/views/categories/CategoryPageView.vue'),
    meta: { layout: 'public', title: 'Category' },
  },
  {
    path: '/search',
    name: 'search',
    component: () => import('@/sites/tfs/views/public/SearchResults.vue'),
    meta: { layout: 'public' },
  },
  {
    path: '/product/:code/:slug',
    name: 'product-detail',
    component: () => import('@/sites/tfs/views/galleries/GalleryDetailView.vue'),
    meta: { layout: 'public', title: 'Product' },
  },
  {
    path: '/content/:code/:slug',
    name: 'gallery-content',
    component: () => import('@/sites/tfs/views/content/GalleryContentView.vue'),
    meta: { layout: 'public', title: 'Gallery' },
  },
  {
    path: '/videos',
    name: 'videos',
    component: () => import('@/sites/tfs/modules/video/pages/VideoCatalogView.vue'),
    meta: { layout: 'public' },
  },
  {
    path: '/videos/:id',
    name: 'video-product',
    component: () => import('@/sites/tfs/modules/video/pages/VideoProductView.vue'),
    meta: { layout: 'public', title: 'Video' },
  },
  {
    path: '/portfolio',
    name: 'portfolio',
    component: () => import('@/sites/tfs/views/portfolio/PortfolioView.vue'),
    meta: { layout: 'public', title: 'Portfolio' },
  },
  // Public Profile Route
  {
    path: '/@:slug',
    name: 'public-profile',
    component: () => import('@/sites/tfs/views/Profile/ProfileView.vue'),
    meta: { layout: 'public', title: 'Profile' },
  },
  // Legacy redirect for backward compatibility
  {
    path: '/profile/:slug',
    redirect: to => `/@${to.params.slug}`,
  },
  // My Wishlist Route (canonical)
  {
    path: '/my-wishlist',
    name: 'my-wishlist',
    component: () => import('@/sites/tfs/views/user/WishlistView.vue'),
    meta: { layout: 'public', requiresAuth: true, title: 'My Wishlist' },
  },
  // Legacy redirects for wishlist
  {
    path: '/profile/wishlist',
    redirect: '/my-wishlist',
  },
  {
    path: '/user/wishlist',
    redirect: '/my-wishlist',
  },
  // My Reviews Route (canonical)
  {
    path: '/my-reviews',
    name: 'my-reviews',
    component: () => import('@/sites/tfs/views/user/ReviewsView.vue'),
    meta: { layout: 'public', requiresAuth: true, title: 'My Reviews' },
  },
  // Legacy redirect for reviews
  {
    path: '/user/reviews',
    redirect: '/my-reviews',
  },
  {
    path: '/following',
    name: 'following',
    component: () => import('@/sites/tfs/views/user/FollowingView.vue'),
    meta: { layout: 'public', requiresAuth: true, title: 'Following' },
  },
  {
    path: '/followers',
    name: 'followers',
    component: () => import('@/sites/tfs/views/user/FollowersView.vue'),
    meta: { layout: 'public', requiresAuth: true, title: 'Followers' },
  },
  {
    path: '/cart',
    name: 'cart',
    component: () => import('@/sites/tfs/views/CartView.vue'),
    meta: { layout: 'public', title: 'Shopping Cart' },
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: () => import('@/sites/tfs/modules/checkout/pages/PublicCheckoutPage.vue'),
    meta: { layout: 'public', requiresAuth: true },
  },
  {
    path: '/payment/return',
    name: 'payment-return',
    component: () => import('@/sites/tfs/modules/checkout/pages/PublicPaymentReturnPage.vue'),
    meta: { layout: 'public' },
  },
  {
    path: '/my-orders',
    name: 'orders',
    component: () => import('@/sites/tfs/modules/order/pages/OrdersPage.vue'),
    meta: { layout: 'public', requiresAuth: true },
  },
  {
    path: '/my-orders/:id',
    name: 'order-detail',
    component: () => import('@/sites/tfs/modules/order/pages/OrderDetailPage.vue'),
    meta: { layout: 'public', requiresAuth: true },
  },
  {
    path: '/my-galleries',
    name: 'my-galleries',
    component: () => import('@/sites/tfs/views/user/MyGalleriesView.vue'),
    meta: { layout: 'public', requiresAuth: true },
  },
  {
    path: '/my-videos',
    name: 'my-videos',
    component: () => import('@/sites/tfs/modules/video/pages/PurchasedVideosView.vue'),
    meta: { layout: 'public', requiresAuth: true, title: 'My Videos' },
  },
  // Admin Routes
  {
    path: '/admin',
    redirect: '/admin/dashboard',
    meta: { layout: 'admin', requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'admin-dashboard',
        component: () => import('@/sites/tfs/views/admin/DashboardView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.DASHBOARD] },
      },
      {
        path: 'roles',
        name: 'admin-roles',
        component: () => import('@/sites/tfs/views/admin/roles/RolesListView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.ROLES.VIEW] },
      },
      {
        path: 'roles/create',
        name: 'admin-roles-create',
        component: () => import('@/sites/tfs/views/admin/roles/RoleCreateView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.ROLES.CREATE] },
      },
      {
        path: 'roles/:id',
        name: 'admin-role-detail',
        component: () => import('@/sites/tfs/views/admin/roles/RoleDetailView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.ROLES.VIEW] },
      },
      {
        path: 'roles/:id/edit',
        name: 'admin-role-edit',
        component: () => import('@/sites/tfs/views/admin/roles/RoleEditView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.ROLES.EDIT] },
      },
      {
        path: 'permissions',
        name: 'admin-permissions',
        component: () => import('@/sites/tfs/views/admin/permissions/PermissionsListView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.PERMISSIONS.VIEW] },
      },
      {
        path: 'categories',
        name: 'admin-categories',
        component: () => import('@/sites/tfs/views/admin/categories/CategoriesView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.CATEGORIES.VIEW] },
      },
      {
        path: 'categories/create',
        name: 'admin-categories-create',
        component: () => import('@/sites/tfs/views/admin/categories/CategoryCreateView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.CATEGORIES.CREATE] },
      },
      {
        path: 'categories/:id/edit',
        name: 'admin-categories-edit',
        component: () => import('@/sites/tfs/views/admin/categories/CategoryEditView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.CATEGORIES.EDIT] },
      },
      {
        path: 'redirects',
        name: 'admin-redirects',
        component: () => import('@/sites/tfs/views/admin/redirects/RedirectsListView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.REDIRECTS.VIEW] },
      },
      {
        path: 'redirects/create',
        name: 'admin-redirects-create',
        component: () => import('@/sites/tfs/views/admin/redirects/RedirectCreateView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.REDIRECTS.CREATE] },
      },
      {
        path: 'redirects/:id/edit',
        name: 'admin-redirects-edit',
        component: () => import('@/sites/tfs/views/admin/redirects/RedirectEditView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.REDIRECTS.EDIT] },
      },
      {
        path: 'activity-logs',
        name: 'admin-activity-logs',
        component: () => import('@/sites/tfs/views/admin/activity-logs/ActivityLogsDashboardView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.ACTIVITY_LOGS.VIEW] },
      },
      {
        path: 'media/processing',
        name: 'admin-media-processing',
        component: () => import('@/sites/tfs/views/admin/MediaProcessingView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.MEDIA.MONITOR] },
      },
      {
        path: 'galleries',
        name: 'admin-galleries',
        component: () => import('@/sites/tfs/views/admin/galleries/GalleriesListView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.GALLERIES.VIEW] },
      },
      {
        path: 'videos',
        name: 'admin-videos',
        component: () => import('@/sites/tfs/modules/video/pages/AdminVideosPage.vue'),
        meta: {
          layout: 'admin',
          exactAnyPermission: [PERMISSIONS.VIDEOS.UPLOAD_OWN, PERMISSIONS.VIDEOS.UPLOAD_ALL],
        },
      },
      {
        path: 'galleries/trash',
        name: 'admin-galleries-trash',
        component: () => import('@/sites/tfs/views/admin/galleries/GalleriesTrashView.vue'),
        meta: {
          layout: 'admin',
          exactAnyPermission: [PERMISSIONS.GALLERIES.DELETE_OWN, PERMISSIONS.GALLERIES.DELETE_ALL],
        },
      },
      {
        path: 'galleries/create',
        name: 'admin-galleries-create',
        component: () => import('@/sites/tfs/views/admin/galleries/CreateGalleryView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.GALLERIES.CREATE] },
      },
      {
        path: 'galleries/:id',
        name: 'admin-gallery-detail',
        component: () => import('@/sites/tfs/views/admin/galleries/GalleryDetailView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.GALLERIES.VIEW] },
      },
      {
        path: 'orders',
        name: 'admin-orders',
        component: () => import('@/sites/tfs/views/admin/orders/OrdersListView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.ORDERS.VIEW] },
      },
      {
        path: 'orders/:id',
        name: 'admin-order-detail',
        component: () => import('@/sites/tfs/views/admin/orders/OrderDetailView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.ORDERS.VIEW] },
      },
      {
        path: 'promotions',
        name: 'admin-promotions',
        component: () => import('@/sites/tfs/views/admin/promotions/PromotionsListView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.PROMOTIONS.VIEW_ALL] },
      },
      {
        path: 'promotions/create',
        name: 'admin-promotions-create',
        component: () => import('@/sites/tfs/views/admin/promotions/PromotionCreateView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.PROMOTIONS.CREATE_ALL] },
      },
      {
        path: 'promotions/:id/edit',
        name: 'admin-promotions-edit',
        component: () => import('@/sites/tfs/views/admin/promotions/PromotionEditView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.PROMOTIONS.EDIT_ALL] },
      },
      {
        path: 'profiles',
        name: 'admin-profiles',
        component: () => import('@/sites/tfs/views/admin/profiles/ProfilesListView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.PROFILES.VIEW] },
      },
      {
        path: 'profiles/create',
        name: 'admin-profiles-create',
        component: () => import('@/sites/tfs/views/admin/profiles/ProfileCreateView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.PROFILES.CREATE] },
      },
      {
        path: 'profiles/:id/edit',
        name: 'admin-profiles-edit',
        component: () => import('@/sites/tfs/views/admin/profiles/ProfileEditView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.PROFILES.EDIT] },
      },
      {
        path: 'users',
        name: 'admin-users',
        component: () => import('@/sites/tfs/views/admin/users/UsersListView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.USERS.VIEW] },
      },
      {
        path: 'users/:id',
        name: 'admin-user-detail',
        component: () => import('@/sites/tfs/views/admin/users/UserDetailView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.USERS.VIEW] },
      },
      {
        path: 'users/:id/edit',
        name: 'admin-users-edit',
        component: () => import('@/sites/tfs/views/admin/users/UserEditView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.USERS.EDIT] },
      },
      {
        path: 'reviews',
        name: 'admin-reviews',
        component: () => import('@/sites/tfs/views/admin/reviews/ReviewsModerationView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.REVIEWS.MODERATE] },
      },
      {
        path: 'settings',
        name: 'admin-site-settings',
        component: () => import('@/sites/tfs/views/admin/settings/SiteSettingsView.vue'),
        meta: { layout: 'admin', permissions: [PERMISSIONS.ADMIN.SETTINGS.VIEW] },
      },
    ],
  },
  // Catch-all 404 — must be last
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/sites/tfs/views/errors/NotFoundRouteView.vue'),
    meta: { layout: 'public', title: 'Page Not Found' },
  },
];

export function createAppRouter(isSsr: boolean = import.meta.env.SSR, linkPage: LinkPageHost | null = null) {
  const router = createVueRouter({
    history: isSsr ? createMemoryHistory() : createWebHistory(),
    routes: linkPage ? [{ path: '/', name: 'link-page-public', component: LinkPageHostPage, props: linkPage, meta: { layout: 'blank' } }] : routes,
    scrollBehavior(_to, _from, savedPosition) {
      if (savedPosition) return savedPosition;
      return { top: 0 };
    },
  });

  setupGuards(router);

  return router;
}

export const router = createAppRouter();

// Setup navigation guards
export default router;
